import { useMemo, useCallback } from "react";
import { Node, Edge } from "reactflow";

// Duplicate identifier detection hook
export const useDuplicateIdentifierDetection = (
  nodes: Node[],
  edges: Edge[]
) => {
  const duplicateIdentifiers = useMemo(() => {
    const identifierCounts = new Map<string, number>();
    const nodesByIdentifier = new Map<string, string[]>();
    
    // Count identifiers from nodes and track which nodes have each identifier
    // Only check current nodes, not the global identifiers registry to avoid false positives
    nodes.forEach(node => {
      if (node.data.identifier && node.data.identifier.trim() !== "") {
        const identifier = node.data.identifier.trim();
        identifierCounts.set(identifier, (identifierCounts.get(identifier) || 0) + 1);
        
        if (!nodesByIdentifier.has(identifier)) {
          nodesByIdentifier.set(identifier, []);
        }
        nodesByIdentifier.get(identifier)!.push(node.id);
      }
    });
    
    // NOTE: Removed global registry check to prevent false positives
    // The global 'identifiers' array is a historical registry and shouldn't affect 
    // current duplicate detection within the active conversation tree
    
    // Return identifiers that appear more than once with their node IDs
    const duplicates: Array<{identifier: string, nodeIds: string[]}> = [];
    identifierCounts.forEach((count, identifier) => {
      if (count > 1) {
        duplicates.push({
          identifier,
          nodeIds: nodesByIdentifier.get(identifier) || []
        });
      }
    });
    
    return duplicates;
  }, [nodes, edges]); // Removed identifiers since we no longer use it

  const getDuplicateIdentifiers = useCallback(() => duplicateIdentifiers, [duplicateIdentifiers]);
  
  const isIdentifierInUse = useCallback((identifier: string, excludeNodeId?: string) => {
    return nodes.some(node => 
      node.data.identifier === identifier && 
      node.id !== excludeNodeId // Exclude the node being edited
    );
  }, [nodes]);
  
  const getAvailableIdentifiers = useCallback(() => {
    const used = new Set(nodes.map(node => node.data.identifier).filter(Boolean));
    return Array.from({length: 26}, (_, i) => String.fromCharCode(65 + i)).filter(id => !used.has(id));
  }, [nodes]);
  
  const getDisabledIdentifiers = useCallback(() => {
    return nodes.map(node => node.data.identifier).filter(Boolean);
  }, [nodes]);
  
  const hasChildWithIdentifier = useCallback((parentId: string, identifier: string) => {
    const children = edges.filter(edge => edge.source === parentId).map(edge => edge.target);
    return children.some(childId => {
      const childNode = nodes.find(node => node.id === childId);
      return childNode?.data.identifier === identifier;
    });
  }, [nodes, edges]);

  return {
    getDuplicateIdentifiers,
    isIdentifierInUse,
    getAvailableIdentifiers,
    getDisabledIdentifiers,
    hasChildWithIdentifier
  };
};